import { useEffect, useState } from 'react';
import { Badge, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { subscribeSync, type SyncStatus } from '../offline/sync.js';

/**
 * Состояние связи и очереди.
 * Водитель должен видеть, что осмотр сохранён, даже когда сети нет —
 * иначе он решит, что работа пропала, и пройдёт осмотр заново.
 */
export function StatusBar(): React.JSX.Element | null {
  const { t } = useTranslation();
  const [online, setOnline] = useState(navigator.onLine);
  const [sync, setSync] = useState<SyncStatus>({
    running: false,
    pending: 0,
    lastError: null,
    lastSyncAt: null,
  });

  useEffect(() => {
    const goOnline = (): void => setOnline(true);
    const goOffline = (): void => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const unsubscribe = subscribeSync(setSync);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      unsubscribe();
    };
  }, []);

  if (online && sync.pending === 0) return null;

  return (
    <Group gap="xs" p="xs" justify="center" wrap="nowrap">
      {!online && (
        <Badge color="orange" size="lg" variant="filled">
          {t('app.offline')}
        </Badge>
      )}
      {sync.pending > 0 && (
        <Badge color="blue" size="lg" variant="light">
          {t('app.queued', { count: sync.pending })}
        </Badge>
      )}
    </Group>
  );
}
